import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { BarChart, HistoryToggleOff, Assessment, Palette, TextFields, PhoneAndroid, WebOutlined, ViewCarousel } from '@mui/icons-material';
import { AppProvider, Navigation, NavigationPageItem, NavigationSubheaderItem, NavigationDividerItem } from "@toolpad/core/AppProvider";
import { DashboardLayout, SidebarFooterProps } from "@toolpad/core/DashboardLayout";
import Products from "./Screens/Products/Products";
import Orders from "./Screens/Orders/Orders";
import RestaurantSubmissions from "./Screens/RestaurantSubmissions/RestaurantSubmissions";
import { ArrowBack, Inventory, Logout, SupervisedUserCircleRounded, AdminPanelSettings } from "@mui/icons-material";
import { navigationStore } from "../../app/stores/Navigation";
import { useStoreConfig } from "../../contexts/StoreConfigContext";
import { useEffect, useMemo } from "react";
import { useScreenComponent } from "../../app/hooks/custom/useScreenComponent";
import { ScreenComponent } from "../../models/enums/ScreenComponents";
import { ParamListBase, RouteProp, useIsFocused } from "@react-navigation/native";
import AuthService from "../../services/supabase/AuthService";
import { isLarge } from "../../navigation/HomeStack";
import {
    AccountPreview,
    AccountPreviewProps,
    AccountPopoverFooter,
} from "@toolpad/core/Account";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Avatar from "@mui/material/Avatar";
import { userStore } from "../../app/stores/User";
import { PortalRoutes } from "../../models/enums/PortalRoutes";
import { usePortalRouter } from "../../app/hooks/custom/usePortalRouter";
import { appConfigStore } from "../../app/stores/AppConfig";
import { roleManagementStore } from "../../app/stores/RoleManagement";
import { themeColors } from "../../config/constants";
import { ThemeToggle } from "../../components/ThemeToggle";
import Dashboard from "./Screens/Dashboard/Dashboard";
import Roles from './Screens/Roles/Roles';
import Users from './Screens/Users/Users';
import CustomerAnalytics from './Screens/CustomerAnalytics/CustomerAnalytics';
import ProductAnalytics from './Screens/ProductAnalytics/ProductAnalytics';
import Audit from './Screens/Audit/Audit';
import AppHeader from './Screens/AppHeader/AppHeader';
import TypographyScreen from './Screens/Typography/Typography';
import SplashScreen from './Screens/SplashScreen/SplashScreen';
import CustomScreens from './Screens/CustomScreens/CustomScreens';
import ProductDetailSettings from './Screens/ProductDetailSettings/ProductDetailSettings';
import { usePermissions } from '../../app/hooks/custom/usePermissions';

const PageContent = ({ pathname }: { pathname: string }) => {
    const renderContent = () => {
        switch (pathname) {
            case PortalRoutes.Dashboard:
                return <Dashboard />;
            case PortalRoutes.Products:
                return <Products />;
            case PortalRoutes.Orders:
                return <Orders />;
            case PortalRoutes.Users:
                return <Users />;
            case PortalRoutes.Roles:
                return <Roles />;
            case PortalRoutes.Customers:
                return <CustomerAnalytics />;
            case PortalRoutes.ProductAnalytics:
                return <ProductAnalytics />;
            case PortalRoutes.Audit:
                return <Audit />;
            case PortalRoutes.AppHeader:
                return <AppHeader />;
            case PortalRoutes.Typography:
                return <TypographyScreen />;
            case PortalRoutes.SplashScreen:
                return <SplashScreen />;
            case PortalRoutes.ProductDetail:
                return <ProductDetailSettings />;
            case PortalRoutes.RestaurantSubmissions:
                return <RestaurantSubmissions />;
            // Platform Admin screens
            case PortalRoutes.CustomScreens:
                return <CustomScreens />;
            default:
                return <Typography>No content available</Typography>;
        }
    };

    return (
        <Box
            sx={{
                py: 4,
                px: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
            }}
        >
            {renderContent()}
        </Box>
    );
}

export const Portal = ({ route }: { route: RouteProp<ParamListBase, string> }) => {
    const { router } = usePortalRouter(route.params);
    const { updateShowNavigationBar } = navigationStore((state) => state);
    const darkMode = appConfigStore((state) => state.darkMode);
    const { navigateToScreen } = useScreenComponent();
    const { signOut } = AuthService();
    const { user } = userStore((state) => state);
    const { hasScreenAccess } = usePermissions(Portal.name);
    const { config } = useStoreConfig();
    const { effectivePermissions } = roleManagementStore();
    
    // Check if user is platform admin (can manage custom screens across stores)
    const isPlatformAdmin = effectivePermissions?.isPlatformAdmin === true;
    const isFocused = useIsFocused();

    // Guard: Check if user has portal access for THIS store
    useEffect(() => {
        if (isFocused && effectivePermissions) {
            // Platform admins always have access (they can manage custom screens across stores)
            if (effectivePermissions.isPlatformAdmin) {
                return;
            }
            // If user has no allowed screens, they shouldn't be in the portal
            if (!effectivePermissions.allowedScreens || effectivePermissions.allowedScreens.length === 0) {
                console.warn('🚫 [Portal] User has no portal permissions for this store, redirecting to shop');
                console.warn('🚫 [Portal] effectivePermissions:', effectivePermissions);
                // Just redirect, don't sign them out - they might be a regular user on this store
                navigateToScreen(ScreenComponent.Shop);
            }
        }
    }, [isFocused, effectivePermissions, navigateToScreen]);

    useEffect(() => {
        if (isFocused) {
            updateShowNavigationBar(false);
        }
    }, [isFocused, updateShowNavigationBar]);

    const navConfig = useMemo(() => {
        const baseNav: (NavigationPageItem | NavigationSubheaderItem | NavigationDividerItem)[] = [
            {
                segment: "dashboard",
                title: "Dashboard",
                icon: <DashboardIcon />,
            },
            {
                kind: "header",
                title: "Management",
            },
            {
                segment: "products",
                title: "Products",
                icon: <Inventory />,
            },
            {
                segment: "orders",
                title: "Orders",
                icon: <ShoppingCartIcon />,
            },
            {
                segment: "restaurant-submissions",
                title: "Restaurant Clients",
                icon: <RestaurantIcon />,
            },
            {
                segment: "product-detail",
                title: "Product Detail",
                icon: <ViewCarousel />,
            },
            {
                kind: 'header',
                title: 'Administration',
            },
            {
                segment: 'users',
                title: 'User Management',
                icon: <SupervisedUserCircleRounded />,
            },
            {
                segment: 'roles',
                title: 'Role Management',
                icon: <AdminPanelSettings />,
            },
            {
                kind: 'header',
                title: 'Customization',
            },
            {
                segment: 'app-header',
                title: 'App Header',
                icon: <Palette />,
            },
            {
                segment: 'typography',
                title: 'Typography',
                icon: <TextFields />,
            },
            {
                segment: 'splash-screen',
                title: 'Loading Screen',
                icon: <PhoneAndroid />,
            },
            {
                kind: "divider",
            },
            {
                kind: "header",
                title: "Analytics",
            },
            {
                segment: "customers",
                title: "Overview",
                icon: <BarChart />,
            },
            {
                segment: "analytics",
                title: "Products",
                icon: <Assessment />,
            },
            {
                segment: "audit",
                title: "Audit Log",
                icon: <HistoryToggleOff />,
            }
        ];

        // Add Platform Admin section only for platform admins
        if (isPlatformAdmin) {
            baseNav.push(
                { kind: 'divider' },
                { kind: 'header', title: 'Platform Admin' },
                {
                    segment: 'custom-screens',
                    title: 'Custom Screens',
                    icon: <WebOutlined />,
                }
            );
        }

        // Platform admin segments that bypass normal permission checks
        const platformAdminSegments = ['custom-screens'];

        // Filter navigation items based on permissions
        const filteredNav = baseNav.filter(item => {
            if ('segment' in item && item.segment) {
                // Platform admins have access to ALL screens
                if (isPlatformAdmin) {
                    return true;
                }
                // Platform admin segments are only for platform admins
                if (platformAdminSegments.includes(item.segment)) {
                    return false;
                }
                return hasScreenAccess(item.segment);
            }
            return true; // Keep headers and dividers for now
        });

        // Remove headers that have no visible screens after them
        const finalNav: typeof baseNav = [];
        for (let i = 0; i < filteredNav.length; i++) {
            const item = filteredNav[i];

            if (item.kind === "header") {
                // Look ahead to see if there are any segments before the next header/divider
                let hasVisibleScreens = false;
                for (let j = i + 1; j < filteredNav.length; j++) {
                    const nextItem = filteredNav[j];
                    if (nextItem.kind === "header" || nextItem.kind === "divider") {
                        break; // Stop at next section
                    }
                    if ("segment" in nextItem) {
                        hasVisibleScreens = true;
                        break;
                    }
                }
                // Only add header if it has visible screens
                if (hasVisibleScreens) {
                    finalNav.push(item);
                }
            } else {
                finalNav.push(item);
            }
        }

        return finalNav as Navigation;
    }, [hasScreenAccess, isPlatformAdmin]);

    const themeConfig = useMemo(() => {
        const currentTheme = darkMode ? themeColors.dark : themeColors.light;

        return createTheme({
            palette: {
                mode: darkMode ? "dark" : "light",
                primary: {
                    main: config.primaryColor,
                },
                background: {
                    default: currentTheme.background,
                    paper: currentTheme.cardBackground,
                },
                text: {
                    primary: currentTheme.textPrimary,
                    secondary: currentTheme.textSecondary,
                },
                divider: currentTheme.divider,
            },
            components: {
                MuiListItemText: {
                    styleOverrides: {
                        primary: {
                            fontSize: "0.875rem",
                        },
                    },
                },
                MuiTypography: {
                    styleOverrides: {
                        subtitle2: {
                            fontSize: "0.75rem",
                        },
                    },
                },
                MuiListItemButton: {
                    styleOverrides: {
                        root: {
                            '&.Mui-selected': {
                                backgroundColor: darkMode ? 'rgba(144, 202, 249, 0.16)' : 'rgba(25, 118, 210, 0.12)',
                                '& .MuiListItemIcon-root': {
                                    color: darkMode ? '#FFFFFF' : config.primaryColor,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: darkMode ? '#FFFFFF' : config.primaryColor,
                                },
                                '&:hover': {
                                    backgroundColor: darkMode ? 'rgba(144, 202, 249, 0.24)' : 'rgba(25, 118, 210, 0.18)',
                                },
                            },
                        },
                    },
                },
            },
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 600,
                    md: 600,
                    lg: 1200,
                    xl: 1536,
                },
            },
            customColors: currentTheme,
        });
    }, [darkMode, config.primaryColor]);

    // Account sidebar components
    function AccountSidebarPreview(props: AccountPreviewProps & { mini: boolean }) {
        const { handleClick, open, mini } = props;
        return (
            <Stack direction="column" p={0}>
                <Divider />
                <AccountPreview
                    variant={mini ? "condensed" : "expanded"}
                    handleClick={handleClick}
                    open={open}
                />
            </Stack>
        );
    }

    const accounts = useMemo(() => [
        {
            id: 1,
            name: user.info.user_metadata?.firstName + " " + user.info.user_metadata?.lastName,
            email: user.info.email,
            image: user.info.user_metadata?.avatar_url,
            color: "#1976d2", // Primary color
        },
    ], [user]);

    const SidebarFooterAccountPopover = () => {
        return (
            <Stack direction="column">
                <Typography variant="body2" mx={2} mt={1}>
                    Accounts
                </Typography>
                <MenuList>
                    {accounts.map((account) => (
                        <MenuItem
                            key={account?.id ?? ""}
                            component="button"
                            sx={{
                                justifyContent: "flex-start",
                                width: "100%",
                                columnGap: 2,
                            }}
                        >
                            <ListItemIcon>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: "0.95rem",
                                        bgcolor: config.primaryColor,
                                    }}
                                    src={account?.image ?? ""}
                                    alt={account?.name ?? ""}
                                >
                                    {account?.name?.[0] ?? ""}
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    width: "100%",
                                }}
                                primary={account?.name ?? ""}
                                secondary={account?.email ?? ""}
                                primaryTypographyProps={{ variant: "body2" }}
                                secondaryTypographyProps={{ variant: "caption" }}
                            />
                        </MenuItem>
                    ))}
                </MenuList>
                <Divider />
                <AccountPopoverFooter sx={{ flexDirection: "column" }}>
                    <MenuItem
                        onClick={() => navigateToScreen(ScreenComponent.Shop)}
                        sx={{ justifyContent: "flex-start", width: "100%" }}
                    >
                        <ListItemIcon>
                            <ArrowBack fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Back to Store</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            signOut();
                        }}
                        sx={{ justifyContent: "flex-start", width: "100%" }}
                    >
                        <ListItemIcon>
                            <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Sign Out</ListItemText>
                    </MenuItem>
                </AccountPopoverFooter>
            </Stack>
        );
    }

    const createPreviewComponent = (mini: boolean) => {
        function PreviewComponent(props: AccountPreviewProps) {
            return <AccountSidebarPreview {...props} mini={mini} />;
        }
        return PreviewComponent;
    };

    const SidebarFooterAccount = ({ mini }: SidebarFooterProps) => {
        return (
            <Stack direction="column" spacing={0}>
                <Button
                    onClick={() => navigateToScreen(ScreenComponent.Shop)}
                    startIcon={!mini ? <ArrowBack /> : undefined}
                    fullWidth
                    sx={{
                        justifyContent: mini ? "center" : "flex-start",
                        textTransform: "none",
                        py: 2.5,
                        px: mini ? 1 : 3,
                        borderRadius: 0,
                        color: "text.primary",
                        minWidth: mini ? 0 : "auto",
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.08)"
                                    : "rgba(0, 0, 0, 0.04)",
                        },
                    }}
                    title={mini ? "Back to Store" : undefined}
                >
                    {mini ? (
                        <ArrowBack />
                    ) : (
                        <Typography variant="body2">Back to Store</Typography>
                    )}
                </Button>
                <Divider />
                {/* <Account
                    slots={{
                        preview: PreviewComponent,
                        popoverContent: SidebarFooterAccountPopover,
                    }}
                    slotProps={{
                        popover: {
                            transformOrigin: { horizontal: "left", vertical: "bottom" },
                            anchorOrigin: { horizontal: "right", vertical: "bottom" },
                            disableAutoFocus: true,
                            slotProps: {
                                paper: {
                                    elevation: 0,
                                    sx: {
                                        overflow: "visible",
                                        filter: (theme) =>
                                            `drop-shadow(0px 2px 8px ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.32)"})`,
                                        mt: 1,
                                        "&::before": {
                                            content: "\"\"",
                                            display: "block",
                                            position: "absolute",
                                            bottom: 10,
                                            left: 0,
                                            width: 10,
                                            height: 10,
                                            bgcolor: "background.paper",
                                            transform: "translate(-50%, -50%) rotate(45deg)",
                                            zIndex: 0,
                                        },
                                    },
                                },
                            },
                        },
                    }}
                /> */}
            </Stack>
        );
    }

    return (
        <AppProvider
            router={router}
            navigation={navConfig}
            theme={themeConfig}
            branding={{
                logo: <></>,
                title: "Portal",
            }}
            session={{
                user: {
                    name: user.info.user_metadata?.firstName + " " + user.info.user_metadata?.lastName,
                    email: user.info.email,
                    image: user.info.user_metadata?.avatar_url,
                }
            }}

            authentication={{
                signIn: () => { },
                signOut: () => {
                    signOut();
                },
            }}
        >
            <DashboardLayout
                defaultSidebarCollapsed={isLarge() ? false : true}
                sidebarExpandedWidth={isLarge() ? 300 : "85%"}
                slots={{
                    sidebarFooter: SidebarFooterAccount,
                    toolbarActions: ThemeToggle,
                }}
                sx={{
                    backgroundColor: themeConfig.customColors.background,
                }}
            >
                <PageContent pathname={router.pathname} />
            </DashboardLayout>
        </AppProvider>
    );
};